import { useState } from "react";
import { DevAgentChatMessageContent } from "../../chat/DevAgentChatMessageContent";
import { messageHasTableOrFence } from "./chatRichContent";

const userTableMarkdownClass =
  "[&_table]:text-[11px] [&_th]:px-2 [&_th]:py-0.5 [&_td]:px-2 [&_td]:py-0.5 [&_th]:border-primary/20 [&_td]:border-primary/20";

/**
 * Detect the `[File: name (Type)]\n```\n<source>\n```\n\n<question>` envelope
 * that runDevAgentSend.ts builds when the user attaches a file. We strip the
 * code fence (the agent gets the source from the message that's stored
 * server-side, but the user shouldn't see their own attached file echoed back
 * as a giant blue code block) and display only a small chip + the question.
 */
function parseFileAttachment(
  content: string,
): { filename: string; question: string } | null {
  // Matches both:
  //   [File: ClassName (ApexClass)]\n```…```\n<question>   (enriched from DB)
  //   [Uploaded: notes.pdf (pdf, 42 lines)]\n```…```\n<question>
  //   [File: ClassName] <question>                          (display text on send)
  // We strip whatever sits inside the fenced block so the huge blue code
  // bubble doesn't render — the user only sees the filename chip + their
  // question. One or more attachments can appear back-to-back; consume
  // them greedily.
  const re =
    /^(?:\[(?:File|Uploaded): ([^\]]+)\]\n```[a-zA-Z0-9_-]*\n[\s\S]*?\n```\n+)+([\s\S]*)$/;
  const m = content.match(re);
  if (m) return { filename: m[1].trim(), question: (m[2] || "").trim() };
  // Short display format: [File: name] question (no code fence)
  const short = content.match(/^\[(?:File|Uploaded): ([^\]]+)\]\s*([\s\S]*)$/);
  if (short) return { filename: short[1].trim(), question: (short[2] || "").trim() };
  return null;
}

export function ChatUser({
  content,
  timestamp,
}: {
  content: string;
  timestamp?: number;
}) {
  const fileAttachment = parseFileAttachment(content);
  const displayContent = fileAttachment ? fileAttachment.question : content;
  const rich = messageHasTableOrFence(displayContent);
  // When a file is attached the chip already communicates the attachment;
  // showing a "Show more" toggle on top of it reads as visual clutter.
  // Only offer expand/collapse on plain long user messages.
  const isLong = !fileAttachment && displayContent.length > 300;
  const [expanded, setExpanded] = useState(false);
  // Truncate at line boundary to avoid breaking markdown syntax
  const shown = isLong && !expanded
    ? displayContent.slice(0, 250).replace(/\n[^\n]*$/, "") + "\n…"
    : displayContent;
  return (
    <div className="flex justify-end py-1.5">
      <div
        data-chat-user-bubble={rich ? "rich" : "solid"}
        className={`rounded-2xl rounded-br-md px-4 py-2.5 max-w-[85%] text-sm select-text shadow-sm ${
          rich
            ? "bg-primary/20 text-foreground border border-primary/40"
            : "bg-primary text-primary-foreground"
        }`}
      >
        {fileAttachment && (
          <div className="inline-flex items-center gap-1 text-[11px] font-medium bg-primary-foreground/15 text-primary-foreground px-2 py-0.5 rounded mb-1.5">
            📎 {fileAttachment.filename}
          </div>
        )}
        <DevAgentChatMessageContent
          content={shown}
          className={
            rich ? userTableMarkdownClass : "text-primary-foreground"
          }
        />
        {isLong && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[10px] opacity-70 hover:opacity-100 mt-1 underline"
          >
            {expanded ? "Show less" : "Show more"}
          </button>
        )}
        {timestamp && (
          <div className="text-[10px] opacity-60 mt-1 text-right">
            {new Date(timestamp).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        )}
      </div>
    </div>
  );
}
